import { useState, useEffect, useRef } from "react";
import AdminLayout from "../../components/AdminLayout";
import { Video, Plus, Trash2, Upload, Music, Play, Pause, ChevronDown, ChevronRight } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDVDs() {
  const [dvds, setDvds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", artist_name: "", description: "" });
  const [videoFile, setVideoFile] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [playingVideoKey, setPlayingVideoKey] = useState(null); // "dvdId-videoIdx"
  const videoRef = useRef(null);

  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchDvds = async () => {
    try {
      const res = await axios.get(`${API}/dvds`);
      setDvds(res.data);
    } catch (e) {
      console.error("Erro:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDvds(); }, []);

  const handleUpload = async (file, path, contentType) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("path", path);
    formData.append("content_type", contentType);
    const res = await axios.post(`${API}/upload-media`, formData, {
      headers: { ...headers, "Content-Type": "multipart/form-data" },
      timeout: 300000,
    });
    return res.data.url;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.artist_name) return;

    setUploading(true);
    try {
      let coverUrl = "";
      let videoUrl = "";

      if (coverFile) {
        setUploadProgress("Enviando capa...");
        coverUrl = await handleUpload(
          coverFile,
          `arrochaplay/dvds/${form.artist_name.toLowerCase().replace(/\s+/g, "-")}/cover.jpg`,
          coverFile.type
        );
      }

      if (videoFile) {
        setUploadProgress("Enviando vídeo... (pode demorar)");
        videoUrl = await handleUpload(
          videoFile,
          `arrochaplay/videos/${form.artist_name.toLowerCase().replace(/\s+/g, "-")}/dvd-${Date.now()}.mp4`,
          "video/mp4"
        );
      }

      setUploadProgress("Salvando DVD...");
      await axios.post(`${API}/dvds`, {
        title: form.title,
        artist_name: form.artist_name,
        description: form.description,
        cover_image: coverUrl,
        videos: videoUrl ? [{ title: form.title, video_url: videoUrl, duration: "" }] : [],
        type: "dvd",
      }, { headers });

      setForm({ title: "", artist_name: "", description: "" });
      setVideoFile(null);
      setCoverFile(null);
      setShowForm(false);
      fetchDvds();
    } catch (e) {
      console.error("Erro ao criar DVD:", e);
      alert("Erro ao criar DVD");
    } finally {
      setUploading(false);
      setUploadProgress("");
    }
  };

  const handleDelete = async (dvdId) => {
    if (!window.confirm("Deletar este DVD?")) return;
    try {
      await axios.delete(`${API}/dvds/${dvdId}`, { headers });
      fetchDvds();
    } catch (e) {
      console.error("Erro:", e);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-dvds">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Video className="text-[#FF007F]" size={24} />
            <h1 className="text-2xl font-heading font-bold text-white">DVDs & Vídeos</h1>
          </div>
          <button
            onClick={() => setShowForm(!showForm)}
            className="flex items-center gap-2 bg-[#FF007F] text-white px-4 py-2 rounded-lg hover:bg-[#FF3399] transition-colors"
            data-testid="add-dvd-btn"
          >
            <Plus size={18} />
            Novo DVD
          </button>
        </div>

        {showForm && (
          <form onSubmit={handleSubmit} className="bg-[#1a1a1a] rounded-xl p-6 border border-white/10 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-zinc-400 text-sm mb-1 block">Título</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="w-full bg-[#121212] border border-white/10 rounded-lg px-4 py-2 text-white"
                  placeholder="Ex: Pablo - DVD Ao Vivo 2026"
                  data-testid="dvd-title-input"
                  required
                />
              </div>
              <div>
                <label className="text-zinc-400 text-sm mb-1 block">Artista</label>
                <input
                  type="text"
                  value={form.artist_name}
                  onChange={(e) => setForm({ ...form, artist_name: e.target.value })}
                  className="w-full bg-[#121212] border border-white/10 rounded-lg px-4 py-2 text-white"
                  placeholder="Nome do artista"
                  data-testid="dvd-artist-input"
                  required
                />
              </div>
            </div>
            <div>
              <label className="text-zinc-400 text-sm mb-1 block">Descrição</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="w-full bg-[#121212] border border-white/10 rounded-lg px-4 py-2 text-white h-20 resize-none"
                placeholder="Descrição do DVD"
                data-testid="dvd-desc-input"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-zinc-400 text-sm mb-1 block">Capa (imagem)</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setCoverFile(e.target.files[0])}
                  className="w-full text-zinc-400 text-sm"
                  data-testid="dvd-cover-input"
                />
              </div>
              <div>
                <label className="text-zinc-400 text-sm mb-1 block">Vídeo (MP4)</label>
                <input
                  type="file"
                  accept="video/*"
                  onChange={(e) => setVideoFile(e.target.files[0])}
                  className="w-full text-zinc-400 text-sm"
                  data-testid="dvd-video-input"
                />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                type="submit"
                disabled={uploading}
                className="bg-[#FF007F] text-white px-6 py-2 rounded-lg hover:bg-[#FF3399] transition-colors disabled:opacity-50"
                data-testid="dvd-submit-btn"
              >
                {uploading ? uploadProgress : "Salvar DVD"}
              </button>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="text-zinc-400 hover:text-white transition-colors"
              >
                Cancelar
              </button>
            </div>
          </form>
        )}

        {loading ? (
          <p className="text-zinc-500">Carregando...</p>
        ) : dvds.length === 0 ? (
          <p className="text-zinc-500">Nenhum DVD cadastrado.</p>
        ) : (
          <div className="space-y-3">
            {dvds.map((dvd) => {
              const isOpen = expanded === dvd.id;
              const videos = dvd.videos || [];
              return (
                <div
                  key={dvd.id}
                  className="bg-[#1a1a1a] rounded-xl border border-white/10 overflow-hidden"
                  data-testid={`dvd-${dvd.id}`}
                >
                  <div
                    onClick={() => setExpanded(isOpen ? null : dvd.id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setExpanded(isOpen ? null : dvd.id);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    className="w-full flex items-center gap-4 p-4 hover:bg-white/5 transition-colors text-left cursor-pointer"
                  >
                    {dvd.cover_image ? (
                      <img src={dvd.cover_image} alt={dvd.title} className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-[#FF007F]/30 to-[#00E5FF]/30 flex items-center justify-center flex-shrink-0">
                        <Video className="w-7 h-7 text-white/60" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-white font-medium truncate">{dvd.title}</h3>
                      <p className="text-zinc-500 text-sm">
                        {dvd.artist_name} — {videos.length} vídeo{videos.length === 1 ? "" : "s"}
                      </p>
                    </div>
                    {isOpen ? <ChevronDown className="text-zinc-500" size={20} /> : <ChevronRight className="text-zinc-500" size={20} />}
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDelete(dvd.id); }}
                      className="text-zinc-500 hover:text-red-500 transition-colors p-2"
                      title="Excluir DVD inteiro"
                      data-testid={`delete-dvd-${dvd.id}`}
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>

                  {isOpen && (
                    <div className="border-t border-white/10">
                      {videos.length === 0 ? (
                        <p className="text-center text-zinc-600 text-sm py-6">Sem vídeos</p>
                      ) : (
                        <div className="divide-y divide-white/5">
                          {videos.map((video, idx) => {
                            const key = `${dvd.id}-${idx}`;
                            const isPlaying = playingVideoKey === key;
                            const videoSrc = video.video_url?.startsWith("/api/media/")
                              ? `${process.env.REACT_APP_BACKEND_URL}${video.video_url}`
                              : video.video_url;
                            return (
                              <div key={key} className="flex items-start gap-3 p-3" data-testid={`dvd-video-${dvd.id}-${idx}`}>
                                <span className="text-zinc-600 tabular-nums w-6 pt-2 text-sm">{String(idx + 1).padStart(2, "0")}</span>
                                <div className="flex-1 min-w-0">
                                  <p className="text-white text-sm font-medium truncate">{video.title || `Vídeo ${idx + 1}`}</p>
                                  <p className="text-zinc-600 text-xs truncate">{video.video_url}</p>
                                  {isPlaying && videoSrc && (
                                    <video
                                      ref={videoRef}
                                      src={videoSrc}
                                      controls
                                      autoPlay
                                      className="mt-2 w-full max-w-md rounded-lg bg-black"
                                      onEnded={() => setPlayingVideoKey(null)}
                                      onError={() => {
                                        setPlayingVideoKey(null);
                                      }}
                                    />
                                  )}
                                </div>
                                <button
                                  onClick={() => setPlayingVideoKey(isPlaying ? null : key)}
                                  disabled={!videoSrc}
                                  className="w-9 h-9 rounded-full bg-[#FF007F]/15 hover:bg-[#FF007F] text-[#FF007F] hover:text-white disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center transition-all flex-shrink-0"
                                  title={isPlaying ? "Parar" : "Assistir vídeo"}
                                  data-testid={`play-video-${dvd.id}-${idx}`}
                                >
                                  {isPlaying ? <Pause size={14} /> : <Play size={14} className="ml-0.5" />}
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
